import axios from 'axios';
import { apiClient } from './api';
import type { AuthUser } from '../types/user.types';
import type { LoginCredentials, LoginOutcome, LoginSuccess } from '../types/auth.types';
import type { ApiResponse } from '../types/api.types';

/** Legacy alias kept so older imports still compile. New code should use LoginOutcome. */
export type LoginResponse = LoginSuccess;

export async function loginUser(credentials: LoginCredentials): Promise<LoginOutcome> {
  try {
    const { data } = await apiClient.post<ApiResponse<LoginOutcome>>(
      '/api/auth/login',
      credentials
    );
    if (!data.success || !data.data) {
      throw new Error(data.error ?? 'Login failed');
    }
    return data.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const code = error.response?.data?.code as string | undefined;
      const message = error.response?.data?.message as string | undefined;
      throw { code, message: message ?? 'Login failed' };
    }
    throw { code: 'UNKNOWN', message: 'Something went wrong. Please try again or contact IT.' };
  }
}

/**
 * Complete a 2FA-challenged login (step 2). Called from the Login 2FA page
 * with the challenge token stashed in sessionStorage by step 1.
 */
export async function completeTwoFactorLogin(
  twoFactorToken: string,
  code: string,
): Promise<LoginSuccess> {
  try {
    const { data } = await apiClient.post<ApiResponse<LoginSuccess>>(
      '/api/auth/2fa/verify-login',
      { twoFactorToken, code },
    );
    if (!data.success || !data.data) {
      throw new Error(data.error ?? '2FA verification failed');
    }
    return data.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const code = error.response?.data?.code as string | undefined;
      const message = error.response?.data?.message as string | undefined;
      throw { code, message: message ?? '2FA verification failed' };
    }
    throw { code: 'UNKNOWN', message: 'Something went wrong. Please try again.' };
  }
}

export async function getCurrentUser(): Promise<AuthUser> {
  const { data } = await apiClient.get<ApiResponse<AuthUser>>('/api/auth/me');
  if (!data.success || !data.data) {
    throw new Error(data.error ?? 'Failed to fetch user');
  }
  return data.data;
}

export async function logoutUser(): Promise<void> {
  try {
    await apiClient.post('/api/auth/logout');
  } catch {
    // best-effort — clear client state regardless
  }
}

/**
 * Replace the signed-in user's company logo. hr_admin / it_admin only; the
 * server resolves the tenant from the token, so there is no id to pass.
 *
 * Multipart, matching profile.service.ts's photo upload. The returned URL
 * carries a `?t=` cache-buster because the object is upserted at one path.
 */
export async function uploadCompanyLogo(formData: FormData): Promise<{ logoUrl: string }> {
  const { data } = await apiClient.post<ApiResponse<{ logoUrl: string }>>(
    '/api/auth/tenant/logo',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  if (!data.success || !data.data) throw new Error(data.error ?? 'Failed to upload logo');
  return data.data;
}

/** Clear the company logo, falling back to the monogram badge. */
export async function removeCompanyLogo(): Promise<void> {
  await apiClient.delete('/api/auth/tenant/logo');
}

/** Rename the signed-in user's company (hr_admin / it_admin). The server takes the company from the token. */
export async function updateCompanyName(name: string): Promise<{ name: string; short: string }> {
  const { data } = await apiClient.patch<ApiResponse<{ name: string; short: string }>>('/api/auth/tenant', { name });
  if (!data.success || !data.data) throw new Error(data.error ?? 'Failed to rename the company');
  return data.data;
}

export interface PublicBranding { name: string; short: string; logoUrl: string | null }

/** Name and logo for the sign-in page, by company ID (slug). No sign-in needed. */
export async function fetchPublicBranding(slug: string): Promise<PublicBranding | null> {
  try {
    const { data } = await apiClient.get<ApiResponse<PublicBranding>>('/api/auth/branding', { params: { tenant: slug } });
    return data.success && data.data ? data.data : null;
  } catch {
    return null;
  }
}
